import React, {Suspense, useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {v4 as uuidv4} from 'uuid';
import ApiClient from '../../service_clients/ApiClient';
import NobiEventTracker from '../../utils/NobiEventTracker';
import Events from '../../utils/Events';
import Chatbot from "../Chatbot/Chatbot";
import styles from './CartAssistantInjector.module.css';
import {EntryPoint as EntryPoint} from "../Chatbot/EntryPoint";
import GlowOnVisible from "../GlowOnVisible/GlowOnVisible";

const CartAssistantInjector = ({
    merchant,
    assistantConfiguration,
    brandTheme,
    log,
    onSendMessage,
    cartProductVariantInternalId,
    cartProductInternalId,
    setCartProductVariantInternalId,
    setCartProductInternalId }) => {

    const [icebreakerResponse, setIcebreakerResponse] = useState(null);
    const [recommendedCollections, setRecommendedCollections] = useState(null);
    const [itemsInCart, setItemsInCart] = useState(0);

    const containerRef = useRef(null);

    // Effect to check for preexisting cart items and subscribe to cart-addition events
    useEffect(() => {
        if (!assistantConfiguration?.cartAssistant) {
            log && log("No cart drawer selector provided in assistant configuration. Aborting CartAssistantInjector.");
            return;
        }

        window.addEventListener("message", (event) => {
            let details = event.data;
            if (!details || !details.productInternalId || !details.productVariantInternalId) {
                return;
            }

            log && log('Parent received message:', event.data);
            const productInternalId = details.productInternalId;
            const productVariantInternalId = details.productVariantInternalId;

            if (itemsInCart == 0) {
                setCartProductInternalId(productInternalId);
                setCartProductVariantInternalId(productVariantInternalId);
            }
            else {
                setCartProductInternalId(null);
                setCartProductVariantInternalId(null);
            }

            setItemsInCart(itemsInCart + 1);
        });
        log && log("Subscribed to cart addition events");

        retrieveExistingCartItemsAndShowInCartAssistant();
        return () => {};
    }, [assistantConfiguration]);

    useEffect(() => {
        if (!icebreakerResponse && cartProductInternalId && cartProductVariantInternalId) {
            fetchIcebreaker(cartProductInternalId);
        }
    }, [cartProductVariantInternalId, cartProductInternalId]);

    // Effect to render the assistant card when the icebreaker response is available
    useEffect(() => {
        if (!icebreakerResponse || !merchant) {
            return;
        }

        const cartDrawers = document.querySelectorAll(assistantConfiguration.cartAssistant?.containerSelector);
        if (!cartDrawers || cartDrawers.length === 0) {
            log && log(`Cart drawer not found with selector: ${assistantConfiguration.cartAssistant?.containerSelector}`);
            return;
        }

        return injectCartAssistantIntoCartDrawer(cartDrawers[0]);
    }, [icebreakerResponse, merchant, brandTheme, assistantConfiguration, onSendMessage]);

    // Function to fetch the icebreaker message given cart items
    const fetchIcebreaker = (productId) => {
        if (!productId || !merchant) {
            return;
        }

        const conversationId = uuidv4();

        ApiClient.getIcebreaker(
            merchant.id,
            productId,
            conversationId,
            (icebreakerResponseDto, error) => {
                if (error) {
                    log && log('Error fetching icebreaker message: ' + error);
                    return;
                }

                if (icebreakerResponseDto) {
                    setIcebreakerResponse(icebreakerResponseDto.message);
                    setRecommendedCollections(icebreakerResponseDto.categories);
                }
            }
        );
    };

    // Check for preexisting cart items (if using Shopify)
    function retrieveExistingCartItemsAndShowInCartAssistant() {
        if (window.Shopify) {
            fetch("/cart.js")
                .then(res => res.json())
                .then(data => {
                    log && log("Fetched cart items");
                    log && log(data);
                    if (data?.items && data.items.length > 0 && !icebreakerResponse) {
                        if (data.items.length == 1) {
                            setCartProductVariantInternalId(data.items[0].variant_id);
                            setCartProductInternalId(data.items[0].product_id);
                            fetchIcebreaker(data.items[0].product_id);
                        }
                        setItemsInCart(data.items.length);
                    }
                })
                .catch(error => {
                    log && log('Error fetching cart items: ' + error);
                });
        }
    }

    function sendMessageWithInitialCategories(iceBreaker, userMessage) {
        onSendMessage(iceBreaker, userMessage, recommendedCollections);
    }

    function addObserverForCartAssistant(cartDrawer) {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    NobiEventTracker.trackEvent(
                        merchant.id,
                        EntryPoint.Cart,
                        Events.CartDrawerAssistantViewed,
                        false,
                        true,
                        null
                    );
                    observer.disconnect();
                }
            });
        }, {
            root: cartDrawer,      // observe visibility relative to the cart drawer
            threshold: 0.1,
            rootMargin: '0px',
        });

        observer.observe(containerRef.current);
        return observer;
    }

    function injectCartAssistantIntoCartDrawer(cartDrawer) {
        // Create a container element to host the assistant card
        const container = document.createElement('div');
        containerRef.current = container;
        cartDrawer.appendChild(container);

        const observer = addObserverForCartAssistant(cartDrawer);

        ReactDOM.render(
            <Suspense fallback={<div>Loading...</div>} ref={containerRef}>
                <div id={styles.cartAssistantContainer}>
                    {assistantConfiguration?.cartAssistant?.header && <h4>{assistantConfiguration?.cartAssistant?.header}</h4>}
                    <GlowOnVisible root={cartDrawer}>
                        <div id={styles.cartAssistantChatbotWrapper} ref={containerRef}>
                            <Chatbot
                                merchant={merchant}
                                initialAssistantMessage={icebreakerResponse}
                                brandTheme={brandTheme}
                                isFirstMessageFromSearchBar={false}
                                showSuggestions={false}
                                entryPoint={EntryPoint.Cart}
                                onSendMessage={onSendMessage ? sendMessageWithInitialCategories : null}
                                initialCategories={recommendedCollections}
                                assistantConfiguration={assistantConfiguration}
                                cartProductVariantInternalId={cartProductVariantInternalId}
                            />
                        </div>
                    </GlowOnVisible>
                </div>
            </Suspense>,
            container
        );

        return () => {
            if (containerRef.current) {
                ReactDOM.unmountComponentAtNode(containerRef.current);
                containerRef.current.remove();
                containerRef.current = null;
            }

            observer.disconnect();
        };
    }

    return null;  // This component doesn't render anything because it injects the assistant card into the cart drawer
};

export default CartAssistantInjector;
